import { useState, useEffect, useMemo } from "react";
import ImportContactsModal from "../../../import-contacts-modal";
import { Box, Typography, Stack, Button, Divider, TextField, Avatar } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IconButton, Select, MenuItem, LinearProgress, InputAdornment } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useContacts, useAgentMutation, useAllContacts } from "../../api";
import { BeatLoader } from "react-spinners";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useAgents } from "../../api";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DeleteContactModal from "../../../delete-contact-modal";
import { exportContactsToXLSX } from "../../functions";
import { formatPhone } from "../../../../../../util";
import { GridOverlay } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuthSession } from "../../../../../../Auth";
import ErrorHandler from "../../error-handling/ErrorHandler";
import { AxiosError } from "axios";
import CustomPagination from "./CustomPagination";
import { ErrorSnackbar } from "../../error-handling";
import { getAgeByDate, formatCurrencyV2, dedupeContactsById } from "../../functions";
import { useDebounce } from "use-debounce";


export default function ContactsTable({
  list,
  selectedAgent,
  onClearAgent,
}: {
  list: List | undefined;
  selectedAgent: Agent | undefined;
  onClearAgent: () => void;
}) {
  const { name: user_name } = useAuthSession();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [editingRow, setEditingRow] = useState<string>("");
  const [newAgentEmail, setNewAgentEmail] = useState<string>("");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 1000);
  const {
    contacts,
    isLoading,
    isFetching,
    total,
    hasNextPage,
    error: contactsError,
    refetch: refetchContacts,
  } = useContacts({
    list_id: list?.id || "",
    page: paginationModel?.page,
    limit: paginationModel?.pageSize,
    hh_id: search,
    agent_id: selectedAgent?.id,
  });

  const {
    data: allContacts,
    isLoading: allContactsLoading,
    error: allContactsError,
    refetch: allContactsRefetch,
  } = useAllContacts({
    list_id: list?.id || "",
  });

  const { data: agents, isLoading: isLoadingAgents } = useAgents();

  const {
    mutate: agentMutate,
    isPending: agentMutatePending,
    error: agentMutateError,
    reset: agentMutateReset,
  } = useAgentMutation({
    onSuccess: () => {
      setEditingRow("");
      setNewAgentEmail("");
    },
  });

  const baseColumns: GridColDef[] = [
    {
      field: "household_number",
      headerName: "Household",
      flex: 1.5,
      sortable: false,
      renderHeader: () => (
        <Typography style={{ paddingLeft: 40, fontSize: 12 }}>
          Household
        </Typography>
      ),

      renderCell: (params) => (
        <Stack flexDirection="row" paddingLeft={2} gap={3}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ width: "2px", backgroundColor: "#EFEFEF", borderRadius: 1 }}
          />
          <TableValue value={params.value} fallback={`HH Not Found`} />
        </Stack>
      ),
    },
    {
      field: "first_name",
      headerName: "Customer",
      flex: 2.2,
      sortable: false,
      renderHeader: () => (
        <Typography style={{ paddingLeft: 40, fontSize: 12 }}>
          Customer
        </Typography>
      ),

      renderCell: (params) => (
        <Stack flexDirection="row" paddingLeft={2} gap={3}>
          <TableValue
            style={{ textTransform: "capitalize" }}
            value={
              params.row.first_name && params.row.last_name
                ? `${params.row?.first_name?.toLowerCase()} ${params.row?.last_name?.toLowerCase()}`
                : null
            }
            fallback={`Name Not Found - ID: ${params.id}`}
          />
        </Stack>
      ),
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1.5,
      renderHeader: () => (
        <Typography style={{ fontSize: 12 }}>Phone</Typography>
      ),
      renderCell: (params) => (
        <TableValue
          value={params.value}
          formatter={formatPhone}
          fallback="Phone Not Found"
        />
      ),
      sortable: false,
    },
    {
      field: "agent_email",
      headerName: "Assigned Agent",
      flex: 3,
      sortable: false,
      renderHeader: () => (
        <Typography style={{ fontSize: 12 }}>Assigned Agent</Typography>
      ),
      renderCell: (params) =>
        isLoadingAgents ||
        (agentMutatePending && editingRow === params.row.id) ? (
          <LinearProgress style={{ width: "60%" }} />
        ) : editingRow === params.row.id ? (
          <Select
            defaultValue={params.value || ""}
            size="small"
            onChange={(e) => setNewAgentEmail(e.target.value)}
            sx={{ width: "95%", height: 30, fontSize: 12 }}
          >
            {agents?.map((v: Agent) => (
              <MenuItem key={v.email} value={v.email}>
                {v.email}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <TableValue value={params.value} fallback="Unassigned" />
        ),
    },

    {
      field: "assignment_start",
      headerName: "Age",
      flex: 1,
      sortable: false,
      renderHeader: () => <Typography style={{ fontSize: 12 }}>Age</Typography>,
      renderCell: (params) => (
        <TableValue
          value={params.value}
          fallback="Age Not Found"
          formatter={getAgeByDate}
        />
      ),
    },
    {
      field: "overdue_balance",
      headerName: "Overdue",
      flex: 1,
      sortable: false,
      renderHeader: () => (
        <Typography style={{ fontSize: 12 }}>Overdue</Typography>
      ),
      renderCell: (params) => (
        <TableValue
          value={params.value}
          fallback="O/B Not Found"
          formatter={(v: string) => {
            const num = parseFloat(v);
            return Number.isNaN(num)
              ? "O/B Not Found"
              : formatCurrencyV2(v) || "";
          }}
        />
      ),
    },
    {
      field: "assignment_created_on",
      headerName: "Added",
      flex: 1.2,
      sortable: false,
      renderHeader: () => (
        <Typography style={{ fontSize: 12 }}>Added</Typography>
      ),
      renderCell: (params) => (
        <TableValue
          value={params.value}
          formatter={(v: string) => {
            return new Date(params.value as string).toLocaleDateString();
          }}
          fallback="Date Added Not Found"
        />
      ),
    },
  ];

  const actionColumn: GridColDef = {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filterable: false,
    flex: 1,
    renderHeader: () => (
      <Typography style={{ fontSize: 12, paddingLeft: 10 }}></Typography>
    ),
    renderCell: (params) => (
      <div style={{ paddingLeft: 12 }}>
        {editingRow === params.row.id ? (
          <IconButton
            size="small"
            color="primary"
            onClick={() => setEditingRow("")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            color="primary"
            onClick={() => setEditingRow(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {editingRow === params.row.id &&
          newAgentEmail &&
          params.row.agent_email !== newAgentEmail && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                const body = {
                  list_name: list?.name || "",
                  agent_email: newAgentEmail,
                  household_id: params.row.household_number as number,
                  contact_id: params.row.id,
                  created_by: user_name,
                };
                agentMutate(body);
              }}
            >
              <CheckIcon fontSize="small" color="success" />
            </IconButton>
          )}
        <DeleteContactModal
          list={list}
          contact={params.row}
          onDeleteFinished={() => {
            setEditingRow("");
            setNewAgentEmail("");
          }}
        />
      </div>
    ),
  };

  // Final columns array
  const columns = list?.end_utc ? baseColumns : [actionColumn, ...baseColumns];

  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;

  const visibleRows = dedupeContactsById(contacts);

  useEffect(() => {
    setSearch("");
  }, [list]);

  return (
    <Box
      sx={{ background: "#fff", borderRadius: 1, padding: 0, paddingTop: 1 }}
    >
      <Stack gap={2}>
        <Stack>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            paddingLeft={3}
            paddingRight={3}
            paddingTop={1}
          >
            <Stack flexDirection="row" alignItems="center" gap={3}>
              <Typography style={{ fontWeight: "bold", fontSize: 16 }}>
                List Contacts
              </Typography>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  style: { height: 35, width: 230 },
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                size="small"
                placeholder="Search Household ID..."
              />
              {selectedAgent && (
                <Stack
                  flexDirection="row"
                  border="1px solid #c7c7c7"
                  borderRadius="6px"
                  height={35}
                  gap={1}
                  alignItems="center"
                  paddingLeft={1}
                  paddingRight={1}
                >
                  <Avatar
                    sx={{
                      bgcolor: selectedAgent.color,
                      color: "#fff",
                      width: 30,
                      height: 30,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        lineHeight: 1,
                      }}
                    >
                      {(() => {
                        const [namePart] = selectedAgent.email.split("@");
                        const parts = namePart.split(".");
                        if (parts.length === 1)
                          return parts[0][0].toUpperCase();
                        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                      })()}
                    </Typography>
                  </Avatar>
                  <IconButton size="small" onClick={onClearAgent} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
            <Stack flexDirection="row" gap={1}>
              {!list?.end_utc && <ImportContactsModal />}
              {allContactsError ? (
                <ErrorHandler
                  error={allContactsError}
                  onRetry={allContactsRefetch}
                />
              ) : (
                <Button
                  onClick={() => exportContactsToXLSX(allContacts)}
                  variant="text"
                  startIcon={<FileDownloadIcon />}
                  disabled={!allContacts || allContacts?.length === 0}
                >
                  Export
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            {contacts?.length ? (
              <Box sx={{ minWidth: "70vw" }}>
                <DataGrid
                  disableColumnFilter
                  rows={visibleRows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  pagination
                  paginationMode="server"
                  rowCount={total}
                  pageSizeOptions={[10]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  loading={isLoading || isFetching}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{
                    loadingOverlay: CustomLoadingOverlay,
                    pagination: () => (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          width: "100%",
                        }}
                      >
                        <CustomPagination isFetching={isFetching} />
                      </Box>
                    ),
                  }}
                  sx={{
                    border: "none",
                    borderRadius: 0,
                    overflow: "hidden",
                    ".MuiDataGrid-virtualScroller": {
                      overflow: "hidden !important",
                    },
                    ".MuiDataGrid-main": {
                      overflowX: "hidden !important",
                    },
                    ".MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      borderRadius: 0,
                    },
                  }}
                />
              </Box>
            ) : contactsError ? (
              <Stack padding={5}>
                <ErrorHandler error={contactsError} onRetry={refetchContacts} />
              </Stack>
            ) : (
              <Box
                sx={{
                  padding: 1,
                  paddingBottom: 2,
                  textAlign: "center",
                  color: "gray",
                }}
              >
                <Typography
                  style={{
                    fontSize: 13,
                    fontWeight: "bold",
                    color: "gray",
                  }}
                >
                  {search
                    ? `No active contacts in List: ${list?.name} - matching Search: "${search}"`
                    : `No active contacts in List: ${list?.name}`}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Stack>
      <ErrorSnackbar
        open={!!agentMutateError}
        error={agentMutateError}
        onClose={() => agentMutateReset()}
      />
    </Box>
  );
}

function CustomLoadingOverlay() {
  return (
    <GridOverlay>
      <CircularProgress />
    </GridOverlay>
  );
}

type TableValueProps = {
  value: string | null | undefined;
  fallback: string;
  style?: React.CSSProperties;
  formatter?: (value: string) => string;
};

const TableValue = ({
  value,
  fallback,
  style = {},
  formatter,
}: TableValueProps) => {
  const displayValue =
    value != null && value !== ""
      ? formatter
        ? formatter(value)
        : value
      : null;
  return (
    <Typography
      style={{
        ...style,
        fontSize: value ? 12 : 11,
        fontStyle: value ? "normal" : "italic",
        color: value ? "#000" : "gray",
      }}
    >
      {displayValue || fallback}
    </Typography>
  );
};